import { identityKey } from './identityKey';
import type { ImportMessage } from './importMessage';
import type {
    PacsTarget,
    RemotePatientIdentity,
    RemoteSeriesRow,
} from './pacsImportModel';

/**
 * The pure layer for looking a patient up in several DICOM sources at once: which sources a lookup
 * runs against, what each answered, which of them hold the same patient, and where each series can
 * be retrieved from. No React, no network.
 *
 * Sources are matched on nothing looser than an exact patient id, and even an exact id is not taken
 * as the same patient when the sources disagree on who that is. Series from two people merged into
 * one retrieval is the failure this module exists to make impossible.
 */

/**
 * The fewest characters a term must have before a lookup is issued as the user types. Shorter terms
 * match too much to be useful and every lookup opens an association on each included source; a user
 * who means a shorter term can still ask for it explicitly.
 */
export const MIN_TYPED_SEARCH_LENGTH = 3;

/** Quiet time after the last keystroke before a typed term is looked up. */
export const SEARCH_DEBOUNCE_MS = 500;

/** Whether a term is long enough to be looked up without the user asking for it. */
export function isTypedSearchReady(term: string): boolean {
    return term.trim().length >= MIN_TYPED_SEARCH_LENGTH;
}

/**
 * The sources a lookup runs against: every queryable source not excluded by the user, in
 * configuration order so results group and default the same way on every visit. A source is
 * remembered as excluded rather than as included, so one added to the configuration later is
 * searched until the user says otherwise.
 */
export function includedSources(
    targets: readonly PacsTarget[],
    excluded: readonly string[]
): string[] {
    const out = new Set(excluded);
    return targets
        .filter((target) => !out.has(target.name))
        .map((target) => target.name);
}

/** What one source answered to the current lookup. */
export type SourceSearch = {
    readonly status: 'searching' | 'answered' | 'failed';
    /**
     * While `searching`, the source's previous answer, kept on screen so results do not flicker empty
     * on every keystroke.
     */
    readonly patients: readonly RemotePatientIdentity[];
    readonly truncated: boolean;
    /** The patients listed answer an earlier term than the one being looked up now. */
    readonly stale: boolean;
    readonly message?: ImportMessage;
};

/** A patient as one particular source listed it. */
export type SourcedPatient = {
    readonly source: string;
    readonly patient: RemotePatientIdentity;
};

/**
 * Whether two entries for a patient can describe the same person. The id must match exactly; a name
 * or a birth date stated by both must agree, compared by {@link identityKey} because sources format
 * the same value differently. A field one entry leaves out is no evidence either way, so it does not
 * by itself make two entries disagree.
 *
 * Not transitive: an entry with no name agrees with two entries naming different people. Whether a
 * set of entries is one person is therefore {@link conflictingPatientIds}'s question, asked of every
 * pair, never of one entry against the rest.
 */
export function sameIdentity(
    a: RemotePatientIdentity,
    b: RemotePatientIdentity
): boolean {
    if (a.dicomPatientId !== b.dicomPatientId) {
        return false;
    }
    return (
        statedValuesAgree(a.dicomPatientName, b.dicomPatientName) &&
        statedValuesAgree(a.patientBirthDate, b.patientBirthDate)
    );
}

function statedValuesAgree(
    a: string | undefined,
    b: string | undefined
): boolean {
    const keyA = identityKey(a ?? '');
    const keyB = identityKey(b ?? '');
    return !keyA || !keyB || keyA === keyB;
}

/**
 * The sources holding the picked patient, in source order: the source it was picked from and every
 * other source listing the same id for the same person.
 *
 * An id the sources disagree about is not combined across sources at all, whichever entry was
 * picked: with one source silent on the name and two others naming different people, the silent
 * one could be either, and no rule of thumb can say which. The retrieval then comes from the
 * picked source alone, so it can never combine two people.
 */
export function patientHolders(
    searches: Readonly<Record<string, SourceSearch>>,
    sourceOrder: readonly string[],
    picked: SourcedPatient
): string[] {
    if (conflictingPatientIds(searches).has(picked.patient.dicomPatientId)) {
        return sourceOrder.filter((source) => source === picked.source);
    }
    return sourceOrder.filter(
        (source) =>
            source === picked.source ||
            (searches[source]?.patients ?? []).some((patient) =>
                sameIdentity(patient, picked.patient)
            )
    );
}

/**
 * The patient ids listed for people who are not the same: any two entries under one id, from any
 * sources, that state a different name or birth date. Each such entry is shown as its own patient
 * with a warning rather than merged, since the id alone cannot say which person a retrieval would
 * bring in.
 */
export function conflictingPatientIds(
    searches: Readonly<Record<string, SourceSearch>>
): ReadonlySet<string> {
    const byId = new Map<string, RemotePatientIdentity[]>();
    for (const search of Object.values(searches)) {
        for (const patient of search.patients) {
            const listed = byId.get(patient.dicomPatientId) ?? [];
            listed.push(patient);
            byId.set(patient.dicomPatientId, listed);
        }
    }
    const conflicting = new Set<string>();
    for (const [id, listed] of byId) {
        const disagree = listed.some((patient, index) =>
            listed
                .slice(index + 1)
                .some((other) => !sameIdentity(patient, other))
        );
        if (disagree) {
            conflicting.add(id);
        }
    }
    return conflicting;
}

/** A series with the sources that hold it, in source order. */
export type SourcedSeriesRow = RemoteSeriesRow & {
    readonly sources: readonly string[];
};

/**
 * Merges the series each holder listed into one list keyed by Series Instance UID, which DICOM
 * defines as globally unique: the same UID from two sources is the same series. Rows keep the order
 * of the first source that listed them, and a row's details are that source's, so the list does not
 * change when a later source answers with the same series.
 */
export function mergeSourcedSeries(
    listings: readonly {
        readonly source: string;
        readonly rows: readonly RemoteSeriesRow[];
    }[],
    sourceOrder: readonly string[]
): SourcedSeriesRow[] {
    const ordered = [...listings].sort(
        (a, b) => sourceOrder.indexOf(a.source) - sourceOrder.indexOf(b.source)
    );
    const merged = new Map<string, SourcedSeriesRow>();
    for (const { source, rows } of ordered) {
        for (const row of rows) {
            const existing = merged.get(row.key);
            merged.set(
                row.key,
                existing === undefined
                    ? { ...row, sources: [source] }
                    : existing.sources.includes(source)
                      ? existing
                      : { ...existing, sources: [...existing.sources, source] }
            );
        }
    }
    return [...merged.values()];
}

/**
 * Where each series is retrieved from unless the user chooses otherwise: the first source in source
 * order that holds it.
 */
export function defaultRetrieveSources(
    rows: readonly SourcedSeriesRow[]
): ReadonlyMap<string, string> {
    return new Map(rows.map((row) => [row.key, row.sources[0]]));
}

/**
 * The patients to list under each source, in source order, each source with what it answered. A
 * source that has not answered yet is still listed, so the user sees that it is being asked.
 */
export function resultsBySource(
    searches: Readonly<Record<string, SourceSearch>>,
    sourceOrder: readonly string[]
): { readonly source: string; readonly search: SourceSearch }[] {
    return sourceOrder
        .filter((source) => searches[source] !== undefined)
        .map((source) => ({ source, search: searches[source] }));
}
